import { FC, MouseEvent, useRef } from 'react';
import {
  ComponentRendering,
  GetStaticComponentProps,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';

import { mapSharedPropertiesToFields } from '@gn-audio/jss-components';
import { SitecoreFieldTypes } from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';
import { withTheme } from 'common/jabraTheme/withTheme';
import { IntroSectionItem } from 'composables/Product/IntroSection/IntroSectionItem';

import { IntroSectionFields, IntroSectionProps } from './introSectionTypes';

export const getStaticProps: GetStaticComponentProps = async (rendering: ComponentRendering) => {
  try {
    const { fields: dataSource, params = {} } = rendering;

    const introSpotData = dataSource as SitecoreFieldTypes;
    const rawFields = mapSharedPropertiesToFields<IntroSectionFields>(introSpotData);
    const fields = {
      ...rawFields,
      eyebrowText: {
        ...rawFields.eyebrowText,
        value: sanitize((rawFields.eyebrowText?.value as string) || ''),
      },
      title: {
        ...rawFields.title,
        value: sanitize((rawFields.title?.value as string) || ''),
      },
      description: {
        ...rawFields.description,
        value: sanitize((rawFields.description?.value as string) || ''),
      },
      items:
        rawFields.items?.map((item) => ({
          ...item,
          title: {
            ...item.title,
            value: sanitize((item.title?.value as string) || ''),
          },
        })) || [],
    };

    return {
      fields,
      params,
    };
  } catch (error) {
    console.log(error);
    const { params = {} } = rendering;
    return { params };
  }
};

const IntroSection: FC<IntroSectionProps> = ({ fields }: IntroSectionProps) => {
  const lightRef = useRef<HTMLDivElement>(null);
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (lightRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      lightRef.current.style.transform = `translate(${x}px, ${y}px)`;
    }
  };

  const kspDataSource = fields.dataSource;

  const eyebrowText = fields.eyebrowText?.value || kspDataSource?.Title.value || '';
  const title = fields.title?.value || kspDataSource?.Header.value || '';
  const description = fields.description?.value || kspDataSource?.Text.value || '';

  return (
    <div className="bg-theme relative overflow-hidden" onMouseMove={handleMouseMove}>
      <div
        ref={lightRef}
        className="pointer-events-none absolute left-0 top-0 h-0 w-0 rounded-full transition-transform duration-100 [box-shadow:0_0_200px_150px_rgba(255,255,255,0.08),_0_0_400px_300px_rgba(255,255,255,0.03)]"
      ></div>

      <div className="container relative flex max-w-6xl flex-col items-center overflow-hidden">
        {eyebrowText && <Eyebrow text={eyebrowText as string} className="mb-5 items-center" />}
        {title && (
          <h2
            className="heading-2 text-center"
            dangerouslySetInnerHTML={{ __html: title as string }}
          />
        )}
        {description && (
          <p
            className="paragraph-large text-center"
            dangerouslySetInnerHTML={{ __html: description as string }}
          />
        )}
        {fields.items.length > 0 && (
          <ul className="mt-6 grid w-full grid-cols-1 gap-4 sm:grid-cols-3">
            {fields.items.map((el) => (
              <IntroSectionItem key={el.id} {...el} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default withTheme({
  defaultTheme: {
    backgroundColor: 'white',
    foregroundColor: 'black',
    darkTheme: false,
  },
})(withDatasourceCheck()<IntroSectionProps>(IntroSection));
